// Calculates and displays the expected delivery date and order cut-off countdown for in-stock products.
// Cut-off hour, lowest transit days, transit days, and excluded dates are read from data attributes set in product-delivery-information.liquid,
// which reads from the Country metaobject matched by the customer's country code.
// Excluded dates are passed as a comma-separated YYYY-MM-DD string.

if (!customElements.get('delivery-estimation')) {
  class DeliveryEstimation extends HTMLElement {
    constructor() {
      super();

      this.deliveryDayEl = this.querySelector('.js-delivery-day');
      this.countdownTimerEl = this.querySelector('.js-delivery-time');

      this.cutoffHour = parseInt(this.dataset.cutoffHour, 10) || 12;

      // Earliest possible delivery time in business days.
      // Falls back to 1 if no Lowest Transit Days value is provided.
      this.lowestTransitDays =
        parseInt(this.dataset.lowestTransitDays, 10) || 1;

      // Latest possible delivery time in business days.
      // Falls back to lowestTransitDays if no Transit Days value is provided.
      this.transitDays =
        parseInt(this.dataset.transitDays, 10) || this.lowestTransitDays;

      this.excludedDates = this.dataset.excluded
        ? this.dataset.excluded.split(',').filter(d => d.trim())
        : [];

      this.renderDeliveryInfo();

      // Recalculate every second so the cut-off countdown and delivery dates
      // update automatically when the cut-off time passes.
      this.interval = setInterval(
        this.renderDeliveryInfo.bind(this),
        1000
      );
    }

    // Clears the countdown interval when the element is removed from the DOM.
    disconnectedCallback() {
      clearInterval(this.interval);
    }

    // Returns today's order cut-off time.
    getCutoffTime() {
      const cutoff = new Date();
      cutoff.setHours(this.cutoffHour, 0, 0, 0);

      return cutoff;
    }

    // The next day an order can actually be dispatched:
    // today if we're still before the cut-off on a working day,
    // otherwise the next working day that isn't an excluded date.
    //
    // Dispatch days are Monday–Friday only.
    getDispatchDate(now) {
      const cutoff = this.getCutoffTime();
      const today = now.getDay();
      const dispatchDate = new Date(now);

      const isWeekday = today >= 1 && today <= 5;

      // If today isn't a weekday, or the cut-off has already passed,
      // move the dispatch date forward.
      if (!(isWeekday && now < cutoff)) {
        let daysToAdd = 1;

        // Friday after cut-off → Monday.
        if (today === 5) {
          daysToAdd = 3;
        }

        // Saturday → Monday.
        else if (today === 6) {
          daysToAdd = 2;
        }

        dispatchDate.setDate(
          dispatchDate.getDate() + daysToAdd
        );
      }

      return this.skipExcludedDates(dispatchDate);
    }

    // Calculates and displays:
    // 1. The countdown until the next dispatch cut-off.
    // 2. The estimated delivery date range.
    renderDeliveryInfo() {
      const now = new Date();

      // Count down to the cut-off on the dispatch day, so the countdown and
      // delivery date below it always refer to the same order.
      //
      // Recalculated each tick so both roll over together when the cut-off
      // passes while the page is still open.
      const dispatchDate = this.getDispatchDate(now);

      const targetCutoff = new Date(dispatchDate);
      targetCutoff.setHours(
        this.cutoffHour,
        0,
        0,
        0
      );

      const distance = Math.max(
        targetCutoff.getTime() - now.getTime(),
        0
      );

      // Over a weekend or excluded run, the cut-off can be more than a day away,
      // so show days as well as hours and minutes.
      const days = Math.floor(
        distance / (1000 * 60 * 60 * 24)
      );

      const hours = Math.floor(
        (distance % (1000 * 60 * 60 * 24)) /
        (1000 * 60 * 60)
      );

      const minutes = Math.floor(
        (distance % (1000 * 60 * 60)) /
        (1000 * 60)
      );

      const parts = [];

      if (days >= 1) {
        parts.push(
          `${days} ${days === 1 ? 'day' : 'days'}`
        );
      }

      if (days >= 1 || hours >= 1) {
        parts.push(
          `${hours} ${hours === 1 ? 'hr' : 'hrs'}`
        );
      }

      parts.push(
        `${minutes} ${minutes === 1 ? 'min' : 'mins'}`
      );

      const countdown = parts.join(' ');

      if (
  this.countdownTimerEl &&
  this.countdownTimerEl.textContent !== countdown
) {
  this.countdownTimerEl.textContent = countdown;
}

if (this.deliveryDayEl) {
  const deliveryDay = this.getDeliveryDay(dispatchDate);

  if (this.deliveryDayEl.textContent !== deliveryDay) {
    this.deliveryDayEl.textContent = deliveryDay;
  }
}
    }

    // Calculates the estimated delivery date range.
    //
    // lowestTransitDays = earliest possible delivery date.
    // transitDays       = latest possible delivery date.
    //
    // Saturday counts as a delivery day.
    // Sunday does not count as a delivery day.
    getDeliveryDay(dispatchDate) {
      const calculateDeliveryDate = (transitDays) => {
        let deliveryDate = new Date(dispatchDate);
        let daysAdded = 0;

        while (daysAdded < transitDays) {
          deliveryDate.setDate(
            deliveryDate.getDate() + 1
          );

          const dow = deliveryDate.getDay();

          // JavaScript day numbers:
          // 0 = Sunday
          // 1 = Monday
          // 2 = Tuesday
          // 3 = Wednesday
          // 4 = Thursday
          // 5 = Friday
          // 6 = Saturday
          //
          // Monday–Saturday count as delivery days.
          // Sunday is skipped.
          if (dow !== 0) {
            daysAdded++;
          }
        }

        // If the calculated delivery date is an excluded date,
        // move it forward to the next valid delivery day.
        deliveryDate =
          this.skipExcludedDates(deliveryDate);

        return deliveryDate;
      };

      const earliestDate =
        calculateDeliveryDate(
          this.lowestTransitDays
        );

      const latestDate =
        calculateDeliveryDate(
          this.transitDays
        );

      const formatDate = (date) => {
        const day =
          new Intl.DateTimeFormat(
            'en-GB',
            { weekday: 'long' }
          ).format(date);

        const month =
          new Intl.DateTimeFormat(
            'en-GB',
            { month: 'long' }
          ).format(date);

        return `${day} ${date.getDate()} ${month}`;
      };

      // If both transit values produce the same date,
      // only display the date once.
      if (
        earliestDate.getTime() ===
        latestDate.getTime()
      ) {
        return formatDate(earliestDate);
      }

    // Temporarily show earliest delivery date only.
// To restore the delivery date range, comment out the line below
// and uncomment the range return underneath.
return formatDate(earliestDate);

// return `${formatDate(earliestDate)} – ${formatDate(latestDate)}`;
    }

    // Loops until the date lands on a non-excluded delivery day.
    //
    // Loops rather than using a single pass to handle consecutive excluded dates
    // such as Christmas Day + Boxing Day.
    //
    // Saturday is allowed as a delivery day.
    // Sunday is not.
    skipExcludedDates(date) {
      let result = new Date(date);
      let dateSkipped = true;

      while (dateSkipped) {
        dateSkipped = false;

        for (const entry of this.excludedDates) {
          const [year, month, day] =
            entry
              .trim()
              .split('-')
              .map(Number);

          if (
            result.getDate() === day &&
            result.getMonth() + 1 === month &&
            result.getFullYear() === year
          ) {
            // Move forward one day if this date is excluded.
            result.setDate(
              result.getDate() + 1
            );

            // If that lands on Sunday, move forward again.
            while (result.getDay() === 0) {
              result.setDate(
                result.getDate() + 1
              );
            }

            dateSkipped = true;
            break;
          }
        }
      }

      return result;
    }
  }

  customElements.define(
    'delivery-estimation',
    DeliveryEstimation
  );
}